import { Cross2Icon } from "@radix-ui/react-icons";
import { DatabaseIcon, Terminal } from "lucide-react";
import { observer } from "mobx-react-lite";
import { BaseSyntheticEvent } from "react";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { CacheControl, ToolCall } from "@/types";

const CACHE_OPTIONS: { label: string; value: CacheControl | undefined }[] = [
  { label: "No caching", value: undefined },
  { label: "Cache (5min)", value: { type: "ephemeral" } },
  { label: "Cache (1hr)", value: { type: "ephemeral", ttl: "1h" } },
];

function getCacheLabel(cc: CacheControl | undefined): string | undefined {
  if (!cc) {
    return undefined;
  }
  return cc.ttl === "1h" ? "1h" : "5m";
}

export interface Props {
  toolCalls: ToolCall[];
  onChangeToolCall: (toolCall: ToolCall) => void;
  onDeleteToolCall: (toolCall: ToolCall) => void;
  onAddToolCall: () => void;
  onSetToolCallCacheControl?: (toolCallId: string, cc: CacheControl | undefined) => void;
  readonly?: boolean;
  isAnthropicModel?: boolean;
}

const ToolCalls = ({
  toolCalls,
  onChangeToolCall,
  onDeleteToolCall,
  onAddToolCall,
  onSetToolCallCacheControl,
  readonly = false,
  isAnthropicModel = false,
}: Props) => {
  const hasTools = toolCalls && toolCalls.length > 0;

  const handleChange = (toolCall: ToolCall) => (e: BaseSyntheticEvent) => {
    const key: keyof ToolCall = e.target.name;
    const updatedTool = { ...toolCall };

    if (key.includes("function.")) {
      const functionKey = key.split("function.")[1] as keyof ToolCall["function"];
      updatedTool.function[functionKey] = e.target.value;
    } else {
      updatedTool[key] = e.target.value;
    }

    onChangeToolCall(updatedTool);
  };

  return (
    <div
      className={`relative ml-9 mr-3 box-border flex h-fit w-auto flex-col justify-center rounded-lg bg-gray-50 py-4 pl-4 ${
        (!readonly &&
          "before:absolute before:-left-5 before:bottom-8 before:-z-0 before:inline-block before:h-full before:min-h-3 before:w-9 before:border-b-2 before:border-l-2 before:border-gray-200 before:content-['']") ||
        "mt-2 before:absolute before:-left-5 before:bottom-8 before:-z-0 before:inline-block before:h-full before:min-h-3 before:w-5 before:border-b-2 before:border-l-2 before:border-gray-200 before:content-['']"
      } group-hover:bg-gray-100`}
    >
      {hasTools && (
        <div className="mb-1 w-full px-4">
          {toolCalls.map((toolCall, index) => {
            const onChange = handleChange(toolCall);
            const cacheLabel = getCacheLabel(toolCall.cache_control);
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`tool_${index}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 font-mono font-bold">
                    <Terminal className="mr-1 inline-block h-4 w-4" />
                    {isAnthropicModel && cacheLabel && readonly && (
                      <BasicTooltip content="Prompt cache breakpoint">
                        <span className="flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                          <DatabaseIcon className="h-3 w-3" />
                          {cacheLabel}
                        </span>
                      </BasicTooltip>
                    )}
                    {isAnthropicModel && !readonly && onSetToolCallCacheControl && toolCall._id && (
                      <DropdownMenu>
                        <BasicTooltip content="Prompt cache breakpoint">
                          <DropdownMenuTrigger asChild>
                            <button
                              className={cn(
                                "flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                                cacheLabel
                                  ? "border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                  : "border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                              )}
                              aria-label="Cache control"
                            >
                              <DatabaseIcon className="h-3 w-3" />
                              {cacheLabel ?? "Cache"}
                            </button>
                          </DropdownMenuTrigger>
                        </BasicTooltip>
                        <DropdownMenuContent align="end" className="min-w-[140px]">
                          {CACHE_OPTIONS.map(opt => (
                            <DropdownMenuItem
                              key={opt.label}
                              onSelect={() => onSetToolCallCacheControl(toolCall._id!, opt.value)}
                              className={cn(
                                "text-xs",
                                JSON.stringify(toolCall.cache_control) ===
                                  JSON.stringify(opt.value) && "font-semibold",
                              )}
                            >
                              {opt.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {!readonly && (
                    <Button variant="destructiveLink" onClick={() => onDeleteToolCall(toolCall)}>
                      <Cross2Icon />
                    </Button>
                  )}
                </div>

                <div className="mb-4">
                  <div className="mb-2 flex w-full flex-row">
                    <Input
                      className=" h-9 flex-1 rounded-none border-gray-200 font-mono text-sm focus:ring-0 group-hover:border-gray-300"
                      placeholder="Tool Id"
                      name="id"
                      onChange={onChange}
                      value={toolCall.id}
                      disabled={readonly}
                    />
                    <Input
                      className=" ml-2 h-9 flex-1 rounded-none border-gray-200 font-mono text-sm focus:ring-0 group-hover:border-gray-300"
                      placeholder="Name"
                      onChange={onChange}
                      name="function.name"
                      value={toolCall.function.name}
                      disabled={readonly}
                    />
                  </div>
                  <Textarea
                    className=" h-9 w-full rounded-none border-gray-200 font-mono text-sm focus:ring-0 group-hover:border-gray-300"
                    placeholder="Arguments"
                    onChange={onChange}
                    name="function.arguments"
                    value={toolCall.function.arguments}
                    disabled={readonly}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!readonly && (
        <div>
          <Button onClick={onAddToolCall} variant="outline" size="sm">
            + Tool Call
          </Button>
        </div>
      )}
    </div>
  );
};

export default observer(ToolCalls);
